import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

function getParts(endsAt) {
  const diff = new Date(endsAt) - new Date();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    diff,
  };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function CountdownBar({ endsAt, createdAt }) {
  const [parts, setParts] = useState(() => getParts(endsAt));

  useEffect(() => {
    const id = setInterval(() => setParts(getParts(endsAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  if (!parts) {
    return (
      <div className="flex items-center justify-center gap-2 py-2.5 bg-black/60 border-y border-white/10" data-testid="countdown-ended">
        <Clock size={12} className="text-white/40" />
        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/40">Raffle ended</span>
      </div>
    );
  }

  // Time-remaining progress (needs a start point). Falls back gracefully.
  let pct = null;
  if (createdAt) {
    const total = new Date(endsAt) - new Date(createdAt);
    if (total > 0) pct = Math.max(0, Math.min(100, (parts.diff / total) * 100));
  }

  const urgent = parts.d === 0 && parts.h < 1;
  const accent = urgent ? "#ff4d4d" : "var(--ufr-green)";

  const Unit = ({ v, label }) => (
    <div className="flex flex-col items-center leading-none">
      <span className="font-display text-base md:text-lg" style={{ color: accent }}>{pad(v)}</span>
      <span className="text-[8px] uppercase tracking-[0.12em] text-white/40 mt-0.5">{label}</span>
    </div>
  );

  return (
    <div className="bg-black/60 border-y border-white/10" data-testid="countdown-bar">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-1.5">
          <Clock size={12} style={{ color: accent }} className={urgent ? "animate-pulse" : ""} />
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/50">Ends in</span>
        </div>
        <div className="flex items-center gap-2">
          {parts.d > 0 && (<><Unit v={parts.d} label="days" /><span className="text-white/20 -mt-2">:</span></>)}
          <Unit v={parts.h} label="hrs" />
          <span className="text-white/20 -mt-2">:</span>
          <Unit v={parts.m} label="min" />
          <span className="text-white/20 -mt-2">:</span>
          <Unit v={parts.s} label="sec" />
        </div>
      </div>
      {pct !== null && (
        <div className="h-1 w-full bg-white/5">
          <div
            className="h-full transition-all duration-1000 ease-linear"
            style={{ width: `${pct}%`, background: accent, boxShadow: `0 0 8px ${accent}` }}
          />
        </div>
      )}
    </div>
  );
}
